import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { i18n } from "./i18n";
import { initSentry } from "./observability/sentry";
import { theme } from "./theme";

// Error-tracking браузерных JS-ошибок (#617). DSN/environment/release
// приходят из build-time Vite env (вшиваются в бандл на `npm run build`).
// Пусто (dev / сборка без DSN) → init no-op, Sentry не подключается.
// `release` нормализуем: пустая строка из env → `undefined`.
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE;
initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "dev",
  release: sentryRelease === "" ? undefined : sentryRelease,
});

// Bootstrap SPA. Слои провайдеров идут снаружи внутрь:
//
//   StrictMode → MantineProvider (UI-kit + тема)
//             → Notifications (toast-портал)
//             → QueryClientProvider (server-state, кэш)
//             → I18nextProvider (переводы)
//             → BrowserRouter (роутинг)
//             → App (страницы)
//
// `Notifications` рендерится между `MantineProvider` и
// `QueryClientProvider`: рассчитывает на Mantine-тему/портал, но
// внутри React-tree сам токенов из QueryClient не использует. Его
// глобальный singleton-стор внутри `@mantine/notifications` —
// поэтому `notifications.show(...)` доступен из любого хука вне
// зависимости от того, где сидит компонент в дереве.
//
// `defaultOptions.queries.refetchOnWindowFocus = false` — для
// control plane background-refetch при возврате во вкладку шумит;
// явные `refetch()` в UI читаются предсказуемее.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("root element missing in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <MantineProvider theme={theme}>
      <Notifications />
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            {/* Корневой ErrorBoundary: ловит краши рендера и репортит
                их в Sentry/GlitchTip (если init прошёл), показывая
                fallback вместо белого экрана. */}
            <Sentry.ErrorBoundary
              fallback={<p>Что-то пошло не так. Перезагрузите страницу.</p>}
            >
              <App />
            </Sentry.ErrorBoundary>
          </BrowserRouter>
        </I18nextProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
